/**
 * Geometry for the system map.
 *
 * Station coordinates are public BART data; track paths come from core/bart-geometry.
 * The bay outline is deliberately coarse: it exists to make the map
 * read as the Bay Area at a glance, not to be a coastline.
 */

import { bartLinePaths } from "../core/bart-geometry";

export interface MapStation {
	id: string;
	name: string;
	lat: number;
	lon: number;
}

export const MAP_STATIONS: Record<string, MapStation> = {
	// Yellow line, the part this app cares about
	WCRK: { id: "WCRK", name: "Walnut Creek", lat: 37.905628, lon: -122.067423 },
	LAFY: { id: "LAFY", name: "Lafayette", lat: 37.893394, lon: -122.123801 },
	ORIN: { id: "ORIN", name: "Orinda", lat: 37.878361, lon: -122.183791 },
	ROCK: { id: "ROCK", name: "Rockridge", lat: 37.844364, lon: -122.251435 },
	MCAR: { id: "MCAR", name: "MacArthur", lat: 37.828415, lon: -122.267105 },
	"19TH": { id: "19TH", name: "19th St", lat: 37.80835, lon: -122.268602 },
	"12TH": { id: "12TH", name: "12th St", lat: 37.803664, lon: -122.271604 },
	WOAK: { id: "WOAK", name: "West Oakland", lat: 37.804872, lon: -122.29514 },
	EMBR: { id: "EMBR", name: "Embarcadero", lat: 37.792874, lon: -122.39702 },
	MONT: { id: "MONT", name: "Montgomery", lat: 37.789405, lon: -122.401066 },
	POWL: { id: "POWL", name: "Powell", lat: 37.784471, lon: -122.407974 },
	CIVC: { id: "CIVC", name: "Civic Center", lat: 37.779732, lon: -122.414123 },
	"16TH": { id: "16TH", name: "16th St", lat: 37.765062, lon: -122.419694 },
	"24TH": { id: "24TH", name: "24th St", lat: 37.75247, lon: -122.418143 },
	GLEN: { id: "GLEN", name: "Glen Park", lat: 37.733064, lon: -122.433817 },
	BALB: { id: "BALB", name: "Balboa Park", lat: 37.721585, lon: -122.447506 },
	DALY: { id: "DALY", name: "Daly City", lat: 37.706121, lon: -122.469081 },

	// Continuations, drawn dim
	PHIL: { id: "PHIL", name: "Pleasant Hill", lat: 37.928403, lon: -122.056013 },
	CONC: { id: "CONC", name: "Concord", lat: 37.973737, lon: -122.029095 },
	NCON: { id: "NCON", name: "North Concord", lat: 38.003193, lon: -122.024653 },
	PITT: { id: "PITT", name: "Pittsburg", lat: 38.018914, lon: -121.945154 },
	COLM: { id: "COLM", name: "Colma", lat: 37.684638, lon: -122.466233 },
	SSAN: { id: "SSAN", name: "South SF", lat: 37.664174, lon: -122.444116 },
	SBRN: { id: "SBRN", name: "San Bruno", lat: 37.637761, lon: -122.416287 },
	SFIA: { id: "SFIA", name: "SFO", lat: 37.615966, lon: -122.392409 },
	MLBR: { id: "MLBR", name: "Millbrae", lat: 37.599787, lon: -122.386702 },

	// Background network
	RICH: { id: "RICH", name: "Richmond", lat: 37.936887, lon: -122.353165 },
	DELN: { id: "DELN", name: "El Cerrito del Norte", lat: 37.925655, lon: -122.317269 },
	PLZA: { id: "PLZA", name: "El Cerrito Plaza", lat: 37.903059, lon: -122.299272 },
	NBRK: { id: "NBRK", name: "North Berkeley", lat: 37.874026, lon: -122.283882 },
	DBRK: { id: "DBRK", name: "Downtown Berkeley", lat: 37.870104, lon: -122.268133 },
	ASHB: { id: "ASHB", name: "Ashby", lat: 37.852803, lon: -122.269957 },
	LAKE: { id: "LAKE", name: "Lake Merritt", lat: 37.797484, lon: -122.265609 },
	FTVL: { id: "FTVL", name: "Fruitvale", lat: 37.774963, lon: -122.224274 },
	COLS: { id: "COLS", name: "Coliseum", lat: 37.753661, lon: -122.196869 },
	SANL: { id: "SANL", name: "San Leandro", lat: 37.72226, lon: -122.161311 },
	BAYF: { id: "BAYF", name: "Bay Fair", lat: 37.696924, lon: -122.126514 },
	HAYW: { id: "HAYW", name: "Hayward", lat: 37.669723, lon: -122.087018 },
	SHAY: { id: "SHAY", name: "South Hayward", lat: 37.634375, lon: -122.057189 },
	UCTY: { id: "UCTY", name: "Union City", lat: 37.59063, lon: -122.017388 },
	FRMT: { id: "FRMT", name: "Fremont", lat: 37.557727, lon: -121.976395 },
	WARM: { id: "WARM", name: "Warm Springs", lat: 37.502171, lon: -121.939313 },
	MLPT: { id: "MLPT", name: "Milpitas", lat: 37.410277, lon: -121.891081 },
	BERY: { id: "BERY", name: "Berryessa", lat: 37.368487, lon: -121.874655 },
	CAST: { id: "CAST", name: "Castro Valley", lat: 37.690746, lon: -122.075602 },
	WDUB: { id: "WDUB", name: "West Dublin", lat: 37.699759, lon: -121.92824 },
	DUBL: { id: "DUBL", name: "Dublin", lat: 37.701687, lon: -121.899179 },
};

export interface MapLine {
	id: string;
	color: string;
	stationIds: string[];
	/** Dim continuations sit behind the focus route. */
	dim?: boolean;
}

/** The stretch the rider actually uses. Drawn bright, with station dots and labels. */
export const FOCUS_LINE_STATIONS = [
	"WCRK",
	"LAFY",
	"ORIN",
	"ROCK",
	"MCAR",
	"19TH",
	"12TH",
	"WOAK",
	"EMBR",
	"MONT",
	"POWL",
	"CIVC",
	"16TH",
	"24TH",
	"GLEN",
	"BALB",
	"DALY",
];

export const YELLOW = "#f5c518";
export const BART_KEY_STATIONS = new Set(["CIVC", "GLEN", "ROCK"]);
export const BART_FOCUS_LINE_WIDTH = 4;

const RED = "#e2483f";
const ORANGE = "#e07b2a";
const GREEN = "#3a9d54";
const BLUE = "#2b7fc4";

export const BACKGROUND_LINES: MapLine[] = [
	{ id: "red", color: RED, stationIds: ["RICH", "DELN", "PLZA", "NBRK", "DBRK", "ASHB", "MCAR", "19TH", "12TH", "WOAK", "EMBR", "MONT", "POWL", "CIVC", "16TH", "24TH", "GLEN", "BALB", "DALY", "COLM", "SSAN", "SBRN", "MLBR"] },
	{ id: "orange", color: ORANGE, stationIds: ["RICH", "DELN", "PLZA", "NBRK", "DBRK", "ASHB", "MCAR", "19TH", "12TH", "LAKE", "FTVL", "COLS", "SANL", "BAYF", "HAYW", "SHAY", "UCTY", "FRMT", "WARM", "MLPT", "BERY"] },
	{ id: "green", color: GREEN, stationIds: ["DALY", "BALB", "GLEN", "24TH", "16TH", "CIVC", "POWL", "MONT", "EMBR", "WOAK", "LAKE", "FTVL", "COLS", "SANL", "BAYF", "HAYW", "SHAY", "UCTY", "FRMT", "WARM", "MLPT", "BERY"] },
	{ id: "blue", color: BLUE, stationIds: ["DUBL", "WDUB", "CAST", "BAYF", "SANL", "COLS", "FTVL", "LAKE", "WOAK", "EMBR", "MONT", "POWL", "CIVC", "16TH", "24TH", "GLEN", "BALB", "DALY"] },
];

/** Yellow beyond the useful stretch, so the line does not appear to stop in mid-air. */
export const FOCUS_CONTINUATIONS: MapLine[] = [
	{ id: "yellow-east", color: YELLOW, stationIds: ["WCRK", "PHIL", "CONC", "NCON", "PITT"], dim: true },
	{ id: "yellow-west", color: YELLOW, stationIds: ["DALY", "COLM", "SSAN", "SBRN", "SFIA", "MLBR"], dim: true },
];

/** Shared by SVG and Leaflet; map views add only their journey-specific overlays. */
export const BART_MAP_LINES = [
	...BACKGROUND_LINES.map(line => ({ ...line, weight: 2, opacity: 0.22 })),
	...FOCUS_CONTINUATIONS.map(line => ({ ...line, weight: 3, opacity: 0.35 })),
	{ id: "yellow-focus", color: YELLOW, stationIds: FOCUS_LINE_STATIONS, weight: BART_FOCUS_LINE_WIDTH, opacity: 0.85 },
].map(line => ({ ...line, paths: bartLinePaths(line.stationIds) }));

const trackStations = new Map(BART_MAP_LINES.flatMap(line => line.paths.flatMap((path, index) => [
	[line.stationIds[index], { ...MAP_STATIONS[line.stationIds[index]], ...path[0] }] as const,
	[line.stationIds[index + 1], { ...MAP_STATIONS[line.stationIds[index + 1]], ...path[path.length - 1] }] as const,
])));

export const BART_MAP_STOPS = [...trackStations].map(([id, station]) => {
	const focus = FOCUS_LINE_STATIONS.includes(id);
	const yellow = focus || FOCUS_CONTINUATIONS.some(line => line.stationIds.includes(id));
	return {
		...station,
		radius: BART_FOCUS_LINE_WIDTH * (BART_KEY_STATIONS.has(id) ? 1.5 : 1),
		color: yellow ? "#665300" : "#51616d",
		weight: 1.5,
		opacity: focus ? 1 : 0.65,
		fillColor: yellow ? YELLOW : "#eef2f6",
		fillOpacity: focus ? 1 : 0.65,
	};
});

/**
 * Coarse San Francisco Bay outline, `[lat, lon]`. Deliberately crude and deliberately run off
 * the top and bottom of the frame, so it reads as water rather than as an island.
 */
export const BAY_OUTLINE: Array<[number, number]> = [
	[37.98, -122.52],
	[37.98, -122.36],
	[37.9, -122.335],
	[37.86, -122.325],
	[37.82, -122.305],
	[37.79, -122.28],
	[37.75, -122.235],
	[37.71, -122.2],
	[37.66, -122.16],
	[37.6, -122.12],
	[37.55, -122.08],
	[37.5, -122.03],
	[37.46, -121.98],
	[37.44, -122.02],
	[37.48, -122.1],
	[37.53, -122.18],
	[37.58, -122.26],
	[37.63, -122.33],
	[37.68, -122.37],
	[37.72, -122.385],
	[37.76, -122.385],
	[37.79, -122.392],
	[37.82, -122.4],
	[37.85, -122.44],
];
